import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ChevronDown, Calendar, Building, Lock, Dumbbell, Package, Tag, User, Star, Clock, Settings, Users, QrCode, ShoppingCart, Menu, Images, AlertTriangle, Percent } from "lucide-react";
import { useState } from "react";

interface NavigationSection {
  title: string;
  items: Array<{
    id: string;
    label: string;
    icon: any;
  }>;
}

interface ProviderDashboardNavigationProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
}

const ProviderDashboardNavigation = ({ activeSection, onSectionChange }: ProviderDashboardNavigationProps) => {
  const [sheetOpen, setSheetOpen] = useState(false);

  const navigationSections: Record<string, NavigationSection> = {
    operations: {
      title: "Operations",
      items: [
        { id: "bookings", label: "Bookings", icon: Calendar },
        { id: "customers", label: "Customers", icon: Users },
        { id: "purchases", label: "Purchases", icon: ShoppingCart },
        { id: "time-slots", label: "Time Slots", icon: Clock },
        { id: "availability", label: "Availability", icon: Calendar },
      ]
    },
    setup: {
      title: "Setup & Management",
      items: [
        { id: "rooms", label: "Rooms", icon: Building },
        { id: "facilities", label: "Facilities", icon: Dumbbell },
        { id: "gallery", label: "Gallery", icon: Images },
        { id: "devices", label: "Devices", icon: Lock },
      ]
    },
    business: {
      title: "Business Tools", 
      items: [
        { id: "memberships", label: "Memberships", icon: Package },
        { id: "promotions", label: "Promotions", icon: Percent },
        { id: "coupons", label: "Coupons", icon: Tag },
        { id: "penalties", label: "Penalties", icon: AlertTriangle },
        { id: "waivers", label: "Waivers", icon: User },
        { id: "qr-generator", label: "QR Generator", icon: QrCode },
      ]
    },
    insights: {
      title: "Insights & Settings",
      items: [
        { id: "reviews", label: "Reviews", icon: Star },
        { id: "analytics", label: "Analytics", icon: Calendar },
        { id: "settings", label: "Settings", icon: Settings },
      ]
    }
  };

  const getCurrentSectionTitle = () => {
    for (const section of Object.values(navigationSections)) {
      const item = section.items.find(item => item.id === activeSection);
      if (item) return item.label;
    }
    return "Dashboard";
  };

  const handleSectionSelect = (sectionId: string) => {
    onSectionChange(sectionId);
    setSheetOpen(false);
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <h2 className="text-lg font-semibold">{getCurrentSectionTitle()}</h2>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex flex-wrap gap-2">
            {Object.entries(navigationSections).map(([sectionKey, section]) => (
              <DropdownMenu key={sectionKey}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    {section.title}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuLabel>{section.title}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {section.items.map((item) => (
                    <DropdownMenuItem
                      key={item.id}
                      onClick={() => onSectionChange(item.id)}
                      className={activeSection === item.id ? "bg-gray-100" : ""}
                    >
                      <item.icon className="h-4 w-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
          </div>

          {/* Mobile Navigation - Sheet/Drawer */}
          <div className="lg:hidden">
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <Menu className="h-4 w-4 mr-2" />
                  Navigate
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[85vh] p-0">
                <div className="flex flex-col h-full">
                  <SheetHeader className="p-6 pb-4 border-b bg-white">
                    <SheetTitle>Navigate Dashboard</SheetTitle>
                  </SheetHeader>
                  
                  <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-6">
                      {Object.entries(navigationSections).map(([sectionKey, section]) => (
                        <div key={sectionKey}>
                          <h3 className="font-medium text-gray-900 mb-3 text-sm uppercase tracking-wide">
                            {section.title}
                          </h3>
                          <div className="grid grid-cols-2 gap-3">
                            {section.items.map((item) => (
                              <Button
                                key={item.id}
                                variant={activeSection === item.id ? "default" : "outline"}
                                className="h-16 flex flex-col items-center justify-center space-y-2 text-xs"
                                onClick={() => handleSectionSelect(item.id)}
                              >
                                <item.icon className="h-5 w-5" />
                                <span className="text-center leading-tight">{item.label}</span>
                              </Button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProviderDashboardNavigation;
